import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from './PublicContentPage';

type PolicyPageKey = 'privacy' | 'terms' | 'cookies';

interface PolicySection {
  title: string;
  body: string;
  points?: string[];
}

interface PublicPolicyPageProps {
  pageKey: PolicyPageKey;
  canonicalPath: string;
}

const SUPPORT_CONTACT_HREF = 'mailto:support@ablecomply.com';

export default function PublicPolicyPage({
  pageKey,
  canonicalPath,
}: PublicPolicyPageProps) {
  const { t } = useTranslation('marketing');
  const sections = t(`pages.${pageKey}.sections`, { returnObjects: true }) as PolicySection[];
  const sidebarPoints = t(`pages.${pageKey}.sidebar.points`, { returnObjects: true }) as string[];
  const relatedLinks = pageKey === 'terms'
    ? [
        { href: '/privacy-policy', label: t('pages.terms.relatedLinks.privacy') },
        { href: '/security', label: t('pages.terms.relatedLinks.security') },
        { href: '/methodology', label: t('pages.terms.relatedLinks.methodology') },
        { href: '/sample-report', label: t('pages.terms.relatedLinks.sampleReport') },
      ]
    : pageKey === 'privacy'
      ? [{ href: '/security', label: t('pages.privacy.securityLinkLabel') }]
      : [];

  return (
    <PublicContentPage
      metaTitle={t(`pages.${pageKey}.meta.title`)}
      metaDescription={t(`pages.${pageKey}.meta.description`)}
      canonicalPath={canonicalPath}
      eyebrow={t(`pages.${pageKey}.hero.eyebrow`)}
      title={t(`pages.${pageKey}.hero.title`)}
      intro={t(`pages.${pageKey}.hero.intro`)}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <div className="acc-public-page-meta">
              <div className="acc-public-page-meta-row">
                <div className="acc-public-page-meta-label">{t('pages.shared.updatedLabel')}</div>
                <div className="acc-public-page-meta-value">{t(`pages.${pageKey}.updatedValue`)}</div>
              </div>
            </div>
            <h2>{t(`pages.${pageKey}.sidebar.title`)}</h2>
            <p>{t(`pages.${pageKey}.sidebar.body`)}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.shared.policyContactTitle')}</h2>
            <p>{t('pages.shared.policyContactBody')}</p>
            <div className="acc-public-page-actions">
              <PublicCtaLink href="/contact">{t('footer.company.contact')}</PublicCtaLink>
              <PublicCtaLink href={SUPPORT_CONTACT_HREF} variant="ghost">
                {t('pages.shared.supportAction')}
              </PublicCtaLink>
              {relatedLinks.map((link) => (
                <PublicCtaLink key={link.href} href={link.href} variant="ghost">
                  {link.label}
                </PublicCtaLink>
              ))}
            </div>
          </section>
        </>
      )}
    >
      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </PublicContentPage>
  );
}
